<!-- @format -->
<template>
    <div class="agency-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div
                    v-if="currentStep.title === 'Add Vendor' || currentStep.title === 'Edit Vendor'"
                    class="step"
                >
                    <div class="form-row profile-image">
                        <image-upload
                            id="image-upload"
                            :uploadUrl="uploadUrl"
                            :placeholderImage="placeholderImage"
                            :providedImageUrl="agency.logoUrl"
                            @fileUploadSuccess="updateImageUrl"
                            orientation="horizontal"
                        />
                    </div>
                    <app-border-section class="account-status-section" topText="Account Status">
                        <p>
                            This determines if the account is active. This affects billing, logging
                            in, and functionality of the entire account.
                        </p>
                        <app-switch v-model="agency.isActive" :size="'large'" />
                    </app-border-section>
                    <app-border-section
                        v-if="isOrganizationUser"
                        class="account-status-section"
                        topText="Subscription Plan"
                    >
                        <promo-search
                            v-model="agency.promo"
                            :initialId="
                                agency.promo && agency.promo.id ? agency.promo.id : defaultPromoId
                            "
                        />
                        <div v-if="agency.promo" class="promo-details">
                            <ul>
                                <li>
                                    <strong>Price: </strong>{{ agency.promo.basePrice | currency }}
                                </li>
                                <li>
                                    <strong>Billing Cycle: </strong>
                                    {{
                                        agency.promo.billingCycle
                                            ? `Billed every ` +
                                              agency.promo.billingCycle +
                                              ` month(s)`
                                            : 'Not Billed'
                                    }}
                                </li>
                            </ul>
                        </div>
                    </app-border-section>
                    <div class="form-row">
                        <app-input v-model="agency.name" :label="'Name'" />
                    </div>
                </div>
                <div v-else-if="currentStep.title === 'Saving...'" class="step">
                    <loading-screen-svg />
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="currentStep.action" :loading="currentStep.loading">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const ImageUpload = () => import('@/components/common/image-upload.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppBorderSection = () => import('@/components/common/app-border-section');
const AppSwitch = () => import('@/components/common/app-switch');
const PromoSearch = () => import('@/components/common/promo-search');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'agency-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'image-upload': ImageUpload,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-border-section': AppBorderSection,
        'app-switch': AppSwitch,
        'promo-search': PromoSearch,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            placeholderImage: require('@/assets/images/company-avatar.png'),
            uploadUrl: '/api/Files',

            agency: {
                id: null,
                name: null,
                logoUrl: null,
                isActive: true,
                masterAgencyId: this.widget.options.masterAgencyId,
                promo: {
                    id: null,
                    name: null,
                    basePrice: 0,
                    billingCycle: 0,
                },
            },

            defaultPromoId: 99,
            currentStep: null,

            steps: {
                add: {
                    title: 'Add Vendor',
                    buttonText: 'Save',
                    loading: false,
                    action: this.saveAgency,
                },
                edit: {
                    title: 'Edit Vendor',
                    buttonText: 'Save',
                    loading: false,
                    action: this.saveAgency,
                },
                loading: {
                    title: 'Loading...',
                    buttonText: 'Loading',
                    action: () => {},
                    loading: true,
                },
                saving: {
                    title: 'Saving...',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    computed: {
        isOrganizationUser() {
            return this.$store.getters.isOrganizationUser;
        },
    },

    methods: {
        getAgency() {
            var filterParam = `$filter=Id eq ${this.agency.id}`;
            var selectParam = `$select=Id,Name,LogoImageUrl,IsActive,PromoId`;

            var query = `/service/Agencies?${filterParam}&${selectParam}`;

            this.$api.get(query).then((response) => {
                this.agency.name = response.data.value[0].Name;
                this.agency.logoUrl = response.data.value[0].LogoImageUrl;
                this.agency.isActive = response.data.value[0].IsActive;

                this.agency.promo =
                    response.data.value[0].Promo === null
                        ? null
                        : {
                              id: response.data.value[0].PromoId,
                              name: null,
                              billingCycle: null,
                              basePrice: null,
                          };

                this.goToEditStep();
            });
        },

        saveAgency() {
            this.currentStep = this.steps['saving'];

            if (this.agency.id) {
                this.patchAgency();
            } else {
                this.postAgency();
            }
        },

        postAgency() {
            var request = {
                name: this.agency.name,
                logoImageUrl: this.agency.logoUrl,
                isActive: this.agency.isActive,
                masterAgencyId: this.agency.masterAgencyId,
                promoId: this.agency.promo ? this.agency.promo.id : this.defaultPromoId,
            };

            this.$api
                .post(`/api/Agencies`, request)
                .then(() => {
                    EventBus.$emit('agency:update');
                    this.$helpers.showSuccessNotification('Your vendor has been created.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.goToAddStep();
                    this.$helpers.showFailNotification('Failed to save your vendor.', { error });
                });
        },

        patchAgency() {
            var changes = [
                {
                    name: 'Name',
                    value: this.agency.name,
                },
                {
                    name: 'LogoImageUrl',
                    value: this.agency.logoUrl,
                },
                {
                    name: 'IsActive',
                    value: this.agency.isActive,
                },
                {
                    name: 'PromoId',
                    value: this.agency.promo ? this.agency.promo.id : null,
                },
            ];

            this.$api
                .patch(`/api/Agencies/${this.agency.id}`, changes)
                .then(() => {
                    EventBus.$emit('agency:update');
                    this.$helpers.showSuccessNotification('Your vendor has been updated.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.goToEditStep();
                    this.$helpers.showFailNotification('Failed to save your vendor.', { error });
                });
        },

        updateImageUrl(imageUrl) {
            this.agency.logoUrl = imageUrl;
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        goToAddStep() {
            this.currentStep = this.steps['add'];
        },

        goToEditStep() {
            this.currentStep = this.steps['edit'];
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },

    created() {
        if (this.widget.options && this.widget.options.agencyId) {
            this.currentStep = this.steps['loading'];
            this.agency.id = this.widget.options.agencyId;
            this.getAgency();
        } else {
            this.goToAddStep();
        }
    },
};
</script>

<style>
.agency-widget .step {
    width: 100%;
}

.agency-widget .step .form-row.profile-image {
    justify-content: center;
    align-items: center;
    margin: 0 auto 25px auto;
}

.agency-widget .step .form-row.profile-image .file-input {
    display: none;
}

.agency-widget .step .form-row.profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.agency-widget .action-container {
    display: flex;
}

.agency-widget .action-container .btn {
    margin-left: 15px;
}

.agency-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.agency-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.agency-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.agency-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.agency-widget .loading-screen {
    text-align: center;
}

.agency-widget .app-border-section {
    margin-bottom: 20px;
}

.agency-widget .account-status-section .app-switch .app-switch-container {
    margin: 0 auto;
}

.agency-widget .promo-details {
    margin-top: 1em;
}

.agency-widget .promo-details ul {
    margin: 0;
}
</style>
